<template>
  <div
    class="custom-button"
    ref="customButtonRef"
    :style="getStyleObject"
    :class="[type, effectiveBright ? 'bright' : '', effectiveGradient ? 'gradient' : '', `size-${size}`, `render-to-${renderTo}`, iconActiveClass]"
    @click="click($event)"
  >
    <RenderIcon v-if="iconName" :iconName="iconName" :color="iconActiveColor" :style="iconStyle"></RenderIcon>
    <slot></slot>
    <slot name="popup" :el="customButtonRef" v-if="popup" :open="popupOpen" :handlerPopOpen="handlerPopOpen"></slot>
  </div>
</template>
<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { adaptStyle } from '@/utils/tools'
import RenderIcon from '../icons/RenderIcon.vue'

const emits = defineEmits<{ click: [MouseEvent] }>()

const props = withDefaults(
  defineProps<{
    renderTo?: 'button' | 'text' //渲染方式，默认button
    type?: 'default' | 'success' | 'info' | 'warning' | 'danger' | 'light-yellow'
    size?: 'normal' | 'medium' //标准、中等按钮尺寸，主要调整了字体大小，方便统一尺寸
    bright?: boolean //亮色按钮 [背景白色，文字适配按钮主体类型颜色]
    gradient?: boolean //背景色渐变，与 bright 互斥，默认 gradient = true
    fontSize?: string
    width?: number | string
    height?: number | string
    backgroundColor?: string
    extStyle?: any
    borderRadius?: boolean
    textSpace?: string
    iconName?: string
    iconExt?: {
      topOffset: string
    }
    popup?: boolean
  }>(),
  {
    renderTo: 'button',
    type: 'default',
    gradient: true,
    extStyle: {},
    borderRadius: true,
    textSpace: '0px',
    // activeClass: 'default gradient',
    size: 'normal'
  }
)

// 确保 bright 和 gradient 互斥
const effectiveGradient = computed(() => {
  return props.bright ? false : props.gradient
})

const effectiveBright = computed(() => {
  return props.bright
})

const popupOpen = ref(false)
// watch(
//   () => popupOpen,
//   (newData) => {
//     console.log('popupOpen', popupOpen.value)
//   },
//   { immediate: true }
// )

const click = (e: MouseEvent) => {
  if (props.popup) {
    popupOpen.value = !popupOpen.value
  }
  emits('click', e)
}

const handlerPopOpen = (open: boolean) => {
  popupOpen.value = open
}

const iconActiveClass = computed(() => {
  if (props.iconName) {
    return 'flex-important flex-align-center flex-gap-5'
  }
})

const customButtonRef = ref(null)
const iconActiveColor = computed(() => {
  if (props.iconName) {
    if (!customButtonRef.value) return
    const style = window.getComputedStyle(customButtonRef.value)
    // console.log('text Color:', style.color)
    return style.color
  }
})
const iconStyle = computed(() => {
  if (props.iconName && props.iconExt) {
    let style: { [key: string]: any } = { topOffset: props.iconExt.topOffset }
    if (props.iconExt.topOffset) {
      style['position'] = 'relative'
      style['top'] = props.iconExt.topOffset
    }
    return adaptStyle(style)
    // return filterObjectUndefined(style)
  }
})

// const sizeClass = 'normal'

// const sizeMap = { normal: { 'font-size': '24px' }, medium: { 'font-size': '36px' }, larger: { 'font-size': '50px' } }
const getStyleObject = computed(() => {
  let style: { [key: string]: any } = {
    width: props.width,
    height: props.height,
    backgroundColor: props.backgroundColor,
    fontSize: props.fontSize,
    ...props.extStyle
  }
  // console.log('getStyleObject', style)

  if (typeof props.width == 'number') {
    style['width'] = props.width + 'px'
  }
  if (typeof props.height == 'number') {
    style['height'] = props.height + 'px'
  }
  if (!props.borderRadius) {
    style['border-radius'] = 'unset'
  }
  style['letter-spacing'] = props.textSpace
  style['text-indent'] = props.textSpace

  return adaptStyle(style)
  // const finalStyle = filterObjectUndefined(style)
  // return finalStyle
})
</script>

<style lang="scss" scoped>
$color-default: var(--color-main);
.custom-button {
  font-size: var(--font-size-body);
  font-family: PingFang SC;
  // font-weight: bold;
  color: #ffffff;
  white-space: nowrap;
}

.size-normal {
  font-size: var(--font-size-body);
  height: var(--btn-height);
  line-height: var(--btn-height);
}

.size-medium {
  font-size: calc(var(--font-size-body) + 4px);
  height: calc(var(--btn-height) + 10px);
  line-height: calc(var(--btn-height) + 10px);
}

.render-to-button {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  // min-width: 140px;
  border-radius: 5px;
  padding: 0 20px;
}
.render-to-text {
  cursor: pointer;
  border: 0 !important;
  height: auto;
  width: auto;
  padding: 0;
  background: transparent !important;
  line-height: initial;
  display: inline-block;
  color: var(--color-main);
}

.default {
  background-color: var(--color-main);
  &.bright {
    background-color: white;
    border: 1px solid var(--color-main);
    color: var(--color-main);
  }
  &.gradient {
    background: linear-gradient(90deg, var(--color-main) 0%, #67a7fc 100%);
    border-radius: 5px;
  }
}

$color-success: #67c33a;
.success {
  background-color: $color-success;
  &.bright {
    background-color: white;
    border: 1px solid $color-success;
    color: $color-success;
  }
  &.gradient {
    background: linear-gradient(90deg, $color-success 0%, #82db57 100%);
    border-radius: 5px;
  }
  &.render-to-text {
    color: $color-success;
  }
}

$color-info: #919399;
.info {
  background-color: $color-info;
  &.bright {
    background-color: white;
    border: 1px solid $color-info;
    color: $color-info;
  }
  &.gradient {
    background: linear-gradient(90deg, $color-info 0%, #c2c3c4 100%);
    border-radius: 5px;
  }
  &.render-to-text {
    color: $color-info;
  }
}

$color-warning: #e7a23b;
.warning {
  background-color: $color-warning;
  &.bright {
    background-color: white;
    border: 1px solid $color-warning;
    color: $color-warning;
  }
  &.gradient {
    background: linear-gradient(90deg, $color-warning 0%, #f4cd93 100%);
    border-radius: 5px;
  }
  &.render-to-text {
    color: $color-warning;
  }
}

$color-danger: #f46c6c;
.danger {
  background-color: $color-danger;
  &.bright {
    background-color: white;
    border: 1px solid $color-danger;
    color: $color-danger;
  }
  &.gradient {
    background: linear-gradient(90deg, $color-danger 0%, #eea1a1 100%);
    border-radius: 5px;
  }
  &.render-to-text {
    color: $color-danger;
  }
}

$light-yellow: #ffa200;
.light-yellow {
  background-color: $light-yellow;
  &.bright {
    background-color: white;
    border: 1px solid $light-yellow;
    color: $light-yellow;
  }
  &.gradient {
    background: linear-gradient(90deg, $light-yellow 0%, #ffd300 100%);
    border-radius: 5px;
  }
  &.render-to-text {
    color: $light-yellow;
  }
}
</style>
